/**
 * @file
 * Language switcher dropdown behavior.
 */

((Drupal, once) => {
  /**
   * Initialize submenu dropdown functionality.
   *
   * @param {HTMLElement} parent
   *   The parent element containing the dropdown.
   */
  function initSubmenu(parent) {
    const parentLink = parent.querySelector('.link');
    const dropdown = parent.querySelector('ul');

    if (!parentLink) {
      return;
    }

    /**
     * Handle mouse over events to show dropdown.
     */
    const handleMouseOver = () => {
      parent.classList.add('active');
    };

    /**
     * Handle link click events to toggle dropdown.
     *
     * @param {Event} e
     *   The click event.
     */
    const handleLinkClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      parent.classList.toggle('active');
    };

    /**
     * Handle dropdown click events to prevent closing.
     *
     * @param {Event} e
     *   The click event.
     */
    const handleDropdownClick = (e) => {
      e.stopPropagation();
    };

    /**
     * Handle mouse out events with delay to check hover state.
     */
    const handleMouseOut = () => {
      setTimeout(() => {
        const hoveringDropdown = dropdown && dropdown.matches(':hover');
        const hoveringLink = parentLink.matches(':hover');

        if (!hoveringDropdown && !hoveringLink) {
          parent.classList.remove('active');
        }
      }, 100);
    };

    /**
     * Handle document click events to close dropdown when clicking outside.
     *
     * @param {Event} e
     *   The click event.
     */
    const handleDocumentClick = (e) => {
      if (!parent.contains(e.target)) {
        parent.classList.remove('active');
      }
    };

    /**
     * Handle document keydown events for Escape key.
     *
     * @param {Event} e
     *   The keydown event.
     */
    const handleDocumentKeydown = (e) => {
      if (e.key === 'Escape') {
        parent.classList.remove('active');
      }
    };

    /**
     * Handle link keydown events for Enter and Space keys.
     *
     * @param {Event} e
     *   The keydown event.
     */
    const handleLinkKeydown = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        parent.classList.toggle('active');
      }
    };

    // Add event listeners.
    parentLink.addEventListener('mouseover', handleMouseOver);
    parentLink.addEventListener('click', handleLinkClick);
    parentLink.addEventListener('mouseout', handleMouseOut);
    parentLink.addEventListener('keydown', handleLinkKeydown);

    if (dropdown) {
      dropdown.addEventListener('mouseover', handleMouseOver);
      dropdown.addEventListener('mouseout', handleMouseOut);
      dropdown.addEventListener('click', handleDropdownClick);
    }

    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('keydown', handleDocumentKeydown);

    // Store handlers for cleanup.
    parent.eventHandlers = {
      parentLink: {
        mouseover: handleMouseOver,
        click: handleLinkClick,
        mouseout: handleMouseOut,
        keydown: handleLinkKeydown,
      },
      dropdown: dropdown
        ? {
            mouseover: handleMouseOver,
            mouseout: handleMouseOut,
            click: handleDropdownClick,
          }
        : null,
      document: {
        click: handleDocumentClick,
        keydown: handleDocumentKeydown,
      },
    };
  }

  /**
   * Drupal behavior for language switcher dropdown functionality.
   *
   * @type {Drupal~behavior}
   *
   * @prop {Drupal~behaviorAttach} attach
   *   Attaches the language switcher dropdown behavior.
   * @prop {Drupal~behaviorDetach} detach
   *   Detaches the language switcher dropdown behavior.
   */
  Drupal.behaviors.langSwitcher = {
    /**
     * Attaches the language switcher dropdown behavior.
     *
     * @param {Element} context
     *   The DOM element context.
     * @param {object} settings
     *   The Drupal settings object.
     */
    attach(context) {
      const elements = once(
        'submenu-lang-init',
        '.region-primary-navigation .lang-switcher',
        context,
      );

      elements.forEach((parent) => {
        initSubmenu(parent);
      });
    },

    /**
     * Detaches the language switcher dropdown behavior.
     *
     * @param {Element} context
     *   The DOM element context.
     * @param {object} settings
     *   The Drupal settings object.
     * @param {string} trigger
     *   The trigger for the detach event.
     */
    detach(context, settings, trigger) {
      if (trigger === 'unload') {
        const elements = context.querySelectorAll(
          '.region-primary-navigation .lang-switcher',
        );

        elements.forEach((parent) => {
          if (!parent.eventHandlers) return;

          const {
            parentLink,
            dropdown,
            document: docHandlers,
          } = parent.eventHandlers;
          const linkElement = parent.querySelector('.link');
          const dropdownElement = parent.querySelector('ul');

          // Remove link event listeners.
          if (linkElement && parentLink) {
            linkElement.removeEventListener('mouseover', parentLink.mouseover);
            linkElement.removeEventListener('click', parentLink.click);
            linkElement.removeEventListener('mouseout', parentLink.mouseout);
            linkElement.removeEventListener('keydown', parentLink.keydown);
          }

          // Remove dropdown event listeners.
          if (dropdownElement && dropdown) {
            dropdownElement.removeEventListener(
              'mouseover',
              dropdown.mouseover,
            );
            dropdownElement.removeEventListener('mouseout', dropdown.mouseout);
            dropdownElement.removeEventListener('click', dropdown.click);
          }

          // Remove document event listeners.
          if (docHandlers) {
            document.removeEventListener('click', docHandlers.click);
            document.removeEventListener('keydown', docHandlers.keydown);
          }

          // Clean up stored handlers.
          delete parent.eventHandlers;
        });
      }
    },
  };
})(Drupal, once);
